import React, { useState } from "react";
import DataTableHeader from "@/components/Datatable/components/DataTableHeader/DataTableHeader";
import DataTablePagination from "@/components/Datatable/components/DataTablePagination/DataTablePagination";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { DataTableProps } from "@/components/Datatable/types";
import { ChevronDown, ChevronRight } from "lucide-react";

const DataTable = <T extends object>({
  columns,
  data,
  totalCount,
  pageIndex,
  pageSize,
  onPageChange,
  sorting,
  onSortingChange,
  loading = false,
  error,
  rowKeyExtractor,
  noDataMessage = "No records found",
  expandable = false,
  renderExpandedRow,
  onRowClick,
}: DataTableProps<T>) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set(),
  );

  const toggleRowExpansion = (rowKey: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  return (
    <div className="relative space-y-4">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-800 bg-opacity-75 dark:bg-opacity-75 z-50">
          <LoadingIndicator isFullScreen={false} />
        </div>
      )}

      {error && <div className="text-red-600 text-sm">Error: {error}</div>}

      <div className="rounded-md border border-gray-200 dark:border-slate-700 shadow-sm overflow-x-auto bg-white dark:bg-slate-800">
        <div className="relative w-full">
          <table className="w-full caption-bottom text-sm min-w-full text-gray-900 dark:text-slate-100">
            <DataTableHeader
              columns={columns}
              expandable={expandable}
              onSortingChange={onSortingChange}
              sorting={sorting}
            />

            <tbody className="[&_tr:last-child]:border-0">
              {data.length === 0 ? (
                <tr className="border-b border-gray-200 dark:border-slate-700 transition-colors hover:bg-gray-50 dark:hover:bg-slate-700">
                  <td
                    className="p-10 text-center text-gray-500 dark:text-slate-400"
                    colSpan={expandable ? columns.length + 1 : columns.length}
                  >
                    {noDataMessage}
                  </td>
                </tr>
              ) : (
                data.map((item, index) => {
                  const rowKey = rowKeyExtractor
                    ? rowKeyExtractor(item, index)
                    : index;
                  const isExpanded = expandedRows.has(rowKey);
                  // The id below exists only while the row is open, so the
                  // reference has to come and go with it: pointing
                  // `aria-controls` at an element that is not rendered claims a
                  // relationship a screen reader cannot follow, which is worse
                  // than declaring none. Mirrors the render condition exactly
                  // — without `renderExpandedRow` there is no expanded row to
                  // control even when the flag says open.
                  const expandedRowId =
                    isExpanded && renderExpandedRow
                      ? `datatable-expanded-${rowKey}`
                      : undefined;

                  return (
                    <React.Fragment key={rowKey}>
                      {/* Mouse only, and deliberately so — see the note on
                          `onRowClick` in types.ts. Callers owe their rows a
                          real control in a cell; the row is not it. */}
                      <tr
                        className={`border-b border-gray-200 dark:border-slate-700 transition-colors hover:bg-gray-50 dark:hover:bg-slate-700 ${onRowClick || expandable ? "cursor-pointer" : ""}`}
                        onClick={() => {
                          if (expandable) toggleRowExpansion(rowKey);
                          if (onRowClick) onRowClick(item);
                        }}
                      >
                        {expandable && (
                          <td className="p-4 align-middle w-12 text-gray-500 dark:text-slate-400">
                            {/* A real button, not a bare icon: the row's own
                                onClick is mouse-only, so this is the only way
                                to expand a row from the keyboard, and the only
                                thing that announces the state. */}
                            <button
                              aria-controls={expandedRowId}
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded ? "Collapse row" : "Expand row"
                              }
                              className="flex items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:focus-visible:ring-slate-400"
                              onClick={(event) => {
                                // The row handler would immediately toggle it
                                // back, and would also fire onRowClick.
                                event.stopPropagation();
                                toggleRowExpansion(rowKey);
                              }}
                              type="button"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                        )}
                        {columns.map((column) => {
                          const rawCellKey = column.id || column.accessorKey;
                          const cellKey = String(rawCellKey);

                          const isActionsCell = column.id === "actions";
                          const alignmentClass = isActionsCell
                            ? "text-right"
                            : "text-left";

                          // Controls in an actions cell act on the row's
                          // record, never on the row's own click behaviour:
                          // without this, pressing Download or Archive also
                          // toggled the row open (or fired onRowClick).
                          // Scoped to the actions column, so the expand
                          // button in its own cell — the keyboard path — is
                          // untouched, and tables with no row-level click
                          // behave exactly as before.
                          const stopRowClick =
                            isActionsCell && (onRowClick || expandable)
                              ? (event: React.MouseEvent) =>
                                  event.stopPropagation()
                              : undefined;

                          if (column.cell) {
                            return (
                              <td
                                className={`p-4 align-middle [&:has([role=checkbox])]:pr-0 ${alignmentClass}`}
                                key={cellKey}
                                onClick={stopRowClick}
                              >
                                {column.cell({ row: { original: item } })}
                              </td>
                            );
                          }

                          if (column.accessorKey) {
                            const typedKey = column.accessorKey as keyof T;
                            const cellValue = item[typedKey];
                            const displayValue =
                              cellValue != null ? String(cellValue) : "";

                            return (
                              <td
                                className={`p-4 align-middle [&:has([role=checkbox])]:pr-0 ${alignmentClass}`}
                                key={cellKey}
                                onClick={stopRowClick}
                              >
                                {displayValue}
                              </td>
                            );
                          }

                          return (
                            <td
                              className={`p-4 align-middle ${alignmentClass}`}
                              key={cellKey}
                              onClick={stopRowClick}
                            />
                          );
                        })}
                      </tr>
                      {expandable && isExpanded && renderExpandedRow && (
                        <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                          <td
                            className="p-0 align-middle max-w-0"
                            colSpan={columns.length + 1}
                            id={expandedRowId}
                          >
                            {renderExpandedRow(item)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DataTablePagination
        onPageChange={onPageChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
      />
    </div>
  );
};

export default DataTable;
